/**
 * Breathing Nebula Visualizer - Calm ambient nebula with drifting translucent blobs
 *
 * Layered translucent blobs drift slowly with Lissajous motion, expand/contract on
 * a configurable breath cycle, and react subtly to audio. No flashes, no strobes,
 * no beat detection — designed for extended calm viewing and sleep-safe environments.
 *
 * Uses offscreen canvas trail technique that properly respects the global background
 * controller (solid, gradient, pattern, or image backgrounds).
 *
 * Rendering layers (bottom to top):
 * 1. Trail fade — offscreen canvas composited at reduced opacity over fresh background
 * 2. Vignette — radial gradient from transparent center to dark edges
 * 3. Nebula blobs — radial gradient circles with Lissajous drift + breath oscillation
 * 4. Sparkles — tiny white points spawned near blob centers, driven by treble energy
 *
 * Audio mapping:
 * - Bass (bassEnergy) → subtle blob scale/brightness boost (+12% at full energy)
 * - Treble (highEnergy) → sparkle spawn rate
 * - No beat detection or flash effects (calm-safe design)
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';
import { mapFrequencyData, smoothAmplitudes } from '../lib/audio-utils.js';

// Metadata for manifest generation
export const metadata = {
  name: 'breathing-nebula',
  displayName: 'Breathing Nebula',
  contextType: '2d',
};

// Pre-allocated pool sizes
const MAX_SPARKLES = 150;
const MAX_SPARKLE_SPAWN_PER_FRAME = 15;
const TWO_PI = Math.PI * 2;

class BreathingNebula extends Base2DVisualizer {
  static name = 'breathing-nebula';
  static displayName = 'Breathing Nebula';

  static getConfigUI() {
    return [
      // --- Nebula ---
      { type: 'section', label: 'Nebula' },
      { type: 'range', id: 'blobCount', label: 'Blob Count', min: 4, max: 16, default: 8 },
      { type: 'range', id: 'blobSize', label: 'Blob Size', min: 5, max: 25, default: 15, scale: 0.01 },
      { type: 'range', id: 'driftSpeed', label: 'Drift Speed', min: 1, max: 20, default: 5, scale: 0.1 },
      { type: 'range', id: 'breathPeriod', label: 'Breath Period (s)', min: 6, max: 12, default: 8 },
      { type: 'range', id: 'breathDepth', label: 'Breath Depth', min: 5, max: 30, default: 15, scale: 0.01 },
      { type: 'range', id: 'brightness', label: 'Brightness', min: 20, max: 100, default: 60, scale: 0.01 },
      { type: 'color', id: 'colorStart', label: 'Color Start', default: '#4466aa' },
      { type: 'color', id: 'colorEnd', label: 'Color End', default: '#aa44cc' },

      // --- Effects ---
      { type: 'section', label: 'Effects' },
      { type: 'range', id: 'trailAlpha', label: 'Trail Fade', min: 3, max: 15, default: 5, scale: 0.01 },
      { type: 'range', id: 'glowIntensity', label: 'Glow Intensity', min: 0, max: 40, default: 20 },
      { type: 'range', id: 'vignetteStrength', label: 'Vignette', min: 0, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'sparkleAmount', label: 'Sparkle Amount', min: 0, max: 100, default: 40, scale: 0.01 },

      // --- Safety ---
      { type: 'section', label: 'Safety' },
      { type: 'checkbox', id: 'safeMode', label: 'Safe Mode', default: false },
      { type: 'range', id: 'smoothing', label: 'Audio Smoothing', min: 50, max: 98, default: 85, scale: 0.01 },
    ];
  }

  getDefaultConfig() {
    return {};
  }

  getElementCount() {
    return 32;
  }

  init() {
    this.time = 0;
    this.breathPhase = 0;
    this.isFirstFrame = true;

    // Trail offscreen canvas — stores previous frame for fade compositing
    this._trailCanvas = null;
    this._trailCtx = null;

    // Vignette gradient cache
    this._vignetteGradient = null;
    this._vignetteW = 0;
    this._vignetteH = 0;

    // Color cache (parsed RGB from hex)
    this._csR = 0; this._csG = 0; this._csB = 0;
    this._ceR = 0; this._ceG = 0; this._ceB = 0;
    this._cachedCS = '';
    this._cachedCE = '';
    this._ensureColorCache(true);

    // Blob pool
    this._initBlobs();

    // Sparkle particle pool (fixed-size circular allocation)
    this._initSparklePool();
  }

  onConfigChange(changed) {
    if (changed.blobCount !== undefined) {
      this._initBlobs();
    }
    if (changed.vignetteStrength !== undefined) {
      this._vignetteGradient = null;
    }
    if (changed.colorStart !== undefined || changed.colorEnd !== undefined) {
      this._ensureColorCache(true);
    }
  }

  /**
   * Parse colorStart/colorEnd hex values into RGB components for fast per-frame use.
   */
  _ensureColorCache(force = false) {
    const cs = this.config.colorStart || '#4466aa';
    const ce = this.config.colorEnd || '#aa44cc';

    if (force || this._cachedCS !== cs) {
      this._csR = parseInt(cs.slice(1, 3), 16) || 0;
      this._csG = parseInt(cs.slice(3, 5), 16) || 0;
      this._csB = parseInt(cs.slice(5, 7), 16) || 0;
      this._cachedCS = cs;
    }
    if (force || this._cachedCE !== ce) {
      this._ceR = parseInt(ce.slice(1, 3), 16) || 0;
      this._ceG = parseInt(ce.slice(3, 5), 16) || 0;
      this._ceB = parseInt(ce.slice(5, 7), 16) || 0;
      this._cachedCE = ce;
    }
  }

  /**
   * Initialize blob array with Lissajous drift parameters.
   * Each blob has a unique position, drift frequency, breath phase offset, and color.
   */
  _initBlobs() {
    const count = this.config.blobCount;
    this.blobs = new Array(count);

    for (let i = 0; i < count; i++) {
      const t = count > 1 ? i / (count - 1) : 0.5;
      this.blobs[i] = {
        // Normalized center position (0-1)
        cx: 0.2 + Math.random() * 0.6,
        cy: 0.2 + Math.random() * 0.6,
        // Lissajous drift parameters (unique per blob for organic motion)
        freqX: 0.3 + Math.random() * 0.4,
        freqY: 0.2 + Math.random() * 0.3,
        ampX: 0.08 + Math.random() * 0.12,
        ampY: 0.06 + Math.random() * 0.10,
        phaseX: Math.random() * TWO_PI,
        phaseY: Math.random() * TWO_PI,
        // Breath phase offset so blobs don't breathe in sync
        breathOffset: (i / count) * TWO_PI,
        // Color position along the gradient (0-1)
        colorT: t,
        // Base radius multiplier (randomized slightly per blob)
        baseRadius: 0.8 + Math.random() * 0.4,
        // Cached screen position (updated in _drawBlobs, read in _drawSparkles)
        _screenX: 0,
        _screenY: 0,
      };
    }
  }

  /**
   * Initialize sparkle particle pool with fixed-size slots.
   */
  _initSparklePool() {
    this.sparklePool = new Array(MAX_SPARKLES);
    for (let i = 0; i < MAX_SPARKLES; i++) {
      this.sparklePool[i] = { x: 0, y: 0, life: 0, maxLife: 0, size: 0, alpha: 0, active: false };
    }
    this._nextSparkle = 0;
  }

  /**
   * Ensure the trail offscreen canvas matches the main canvas dimensions.
   */
  _ensureTrailCanvas() {
    const { canvas } = this;
    if (!this._trailCanvas || this._trailCanvas.width !== canvas.width || this._trailCanvas.height !== canvas.height) {
      if (typeof OffscreenCanvas !== 'undefined') {
        this._trailCanvas = new OffscreenCanvas(canvas.width, canvas.height);
      } else {
        this._trailCanvas = document.createElement('canvas');
        this._trailCanvas.width = canvas.width;
        this._trailCanvas.height = canvas.height;
      }
      this._trailCtx = this._trailCanvas.getContext('2d');
    }
  }

  /**
   * Override draw() to produce trail fade that respects the background controller.
   *
   * Instead of overlaying a semi-transparent solid color (which ignores the global
   * background and always converges to that color), we:
   * 1. Save the current canvas (previous frame) to an offscreen buffer
   * 2. Draw the proper background via clearCanvas() -> drawBackground()
   * 3. Composite the saved frame on top at reduced opacity (trail effect)
   * 4. Render new content
   *
   * This works correctly with solid colors, gradients, patterns, and images.
   */
  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();

    // Ensure buffer is properly sized
    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }

    // Map frequency data to element count
    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);

    // Ensure previousAmplitudes is properly sized
    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }

    // Apply smoothing
    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);

    // Store features for convenience getters (bassEnergy, beatDetected, etc.)
    this._currentFeatures = features;

    const { ctx, canvas } = this;

    // safeMode caps trailAlpha at 0.04 for more persistence (slower fade = less flicker)
    // trailAlpha controls fade speed: higher = faster fade, so capping low = more ghosting
    const trailAlpha = this.config.safeMode
      ? Math.min(this.config.trailAlpha, 0.04)
      : this.config.trailAlpha;

    if (this.isFirstFrame) {
      this.clearCanvas();
      this.isFirstFrame = false;
    } else {
      // Trail effect via offscreen canvas compositing
      this._ensureTrailCanvas();

      // 1. Snapshot current canvas (previous frame) into offscreen buffer
      this._trailCtx.clearRect(0, 0, canvas.width, canvas.height);
      this._trailCtx.drawImage(canvas, 0, 0);

      // 2. Draw fresh background (respects background controller)
      this.clearCanvas();

      // 3. Composite previous frame on top at reduced opacity
      //    trailAlpha controls fade speed: higher = faster fade (less persistence)
      ctx.save();
      ctx.globalAlpha = 1 - trailAlpha;
      ctx.drawImage(this._trailCanvas, 0, 0);
      ctx.restore();
    }

    this.renderFrame(amplitudes, features);
  }

  /**
   * Main render: advance time, draw vignette, draw blobs, draw sparkles.
   */
  renderFrame(amplitudes, features) {
    const { ctx, canvas, config } = this;
    const dt = 1 / 60; // approximate frame delta
    this.time += dt;

    // Advance breath phase (full cycle = breathPeriod seconds)
    this.breathPhase += (TWO_PI / config.breathPeriod) * dt;
    // Wrap to prevent floating-point precision loss in long sessions
    if (this.breathPhase > 1000 * TWO_PI) this.breathPhase -= 1000 * TWO_PI;

    // Read audio via mixin getters (ignore beatDetected — calm design)
    const bassE = this.bassEnergy;
    const highE = this.highEnergy;

    // Compute safe-mode overrides as local vars (no per-access branching)
    const effectiveBrightness = config.safeMode
      ? Math.min(config.brightness, 0.50)
      : config.brightness;
    const effectiveSparkleAmount = config.safeMode
      ? Math.min(config.sparkleAmount, 0.20)
      : config.sparkleAmount;
    const effectiveGlow = config.safeMode
      ? Math.min(config.glowIntensity, 15)
      : config.glowIntensity;

    // --- Layer 1: Vignette ---
    if (config.vignetteStrength > 0) {
      this._drawVignette();
    }

    // --- Layer 2: Nebula blobs ---
    this._drawBlobs(dt, bassE, effectiveBrightness, effectiveGlow);

    // --- Layer 3: Sparkles ---
    this._drawSparkles(dt, highE, effectiveSparkleAmount);
  }

  /**
   * Draw radial vignette from transparent center to dark edges.
   * Gradient is cached and only rebuilt on canvas resize.
   */
  _drawVignette() {
    const { ctx, canvas, config } = this;

    if (this._vignetteW !== canvas.width || this._vignetteH !== canvas.height || !this._vignetteGradient) {
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      const radius = Math.max(canvas.width, canvas.height) * 0.7;
      this._vignetteGradient = ctx.createRadialGradient(cx, cy, radius * 0.3, cx, cy, radius);
      this._vignetteGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
      this._vignetteGradient.addColorStop(1, 'rgba(0, 0, 0, 1)');
      this._vignetteW = canvas.width;
      this._vignetteH = canvas.height;
    }

    ctx.save();
    ctx.globalAlpha = config.vignetteStrength;
    ctx.fillStyle = this._vignetteGradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  }

  /**
   * Update and draw all nebula blobs.
   *
   * Each blob:
   * - Drifts via Lissajous curves (two independent sinusoids for X and Y)
   * - Breathes (scale oscillation) with per-blob phase offset
   * - Reacts subtly to bass: +12% scale/brightness at full bass energy
   * - Rendered as a radial gradient circle (source-over body + source-over highlight core with shadowBlur glow)
   */
  _drawBlobs(dt, bassEnergy, brightness, glowIntensity) {
    const { ctx, canvas, config } = this;
    const w = canvas.width;
    const h = canvas.height;
    const minDim = Math.min(w, h);

    for (let i = 0; i < this.blobs.length; i++) {
      const blob = this.blobs[i];

      // Lissajous drift: smooth figure-8-like motion
      const driftX = Math.sin(this.time * config.driftSpeed * blob.freqX + blob.phaseX) * blob.ampX;
      const driftY = Math.sin(this.time * config.driftSpeed * blob.freqY + blob.phaseY) * blob.ampY;

      const x = (blob.cx + driftX) * w;
      const y = (blob.cy + driftY) * h;

      // Cache screen position for sparkle spawning (avoids recomputing trig)
      blob._screenX = x;
      blob._screenY = y;

      // Breath oscillation: sinusoidal scale modulation with per-blob offset
      const breathScale = 1 + Math.sin(this.breathPhase + blob.breathOffset) * config.breathDepth;

      // Bass modulation: subtle scale/brightness boost (+12% at full energy)
      const bassBoost = 1 + bassEnergy * 0.12;

      const radius = Math.max(0, config.blobSize * minDim * blob.baseRadius * breathScale * bassBoost);

      // Skip if radius is zero or blob is off-screen (with generous margin for glow)
      if (radius < 0.5) continue;
      if (x + radius + glowIntensity < 0 || x - radius - glowIntensity > w ||
          y + radius + glowIntensity < 0 || y - radius - glowIntensity > h) {
        continue;
      }

      // Interpolate color for this blob
      const t = blob.colorT;
      const r = Math.round(this._csR + (this._ceR - this._csR) * t);
      const g = Math.round(this._csG + (this._ceG - this._csG) * t);
      const b = Math.round(this._csB + (this._ceB - this._csB) * t);

      // Blob alpha modulated by brightness and bass
      const alpha = brightness * (0.8 + bassEnergy * 0.12);

      // --- Body: radial gradient from colored center to transparent edge ---
      const grad = ctx.createRadialGradient(x, y, 0, x, y, radius);
      grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${alpha * 0.6})`);
      grad.addColorStop(0.4, `rgba(${r}, ${g}, ${b}, ${alpha * 0.3})`);
      grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

      ctx.save();
      if (glowIntensity > 0) {
        ctx.shadowBlur = glowIntensity;
        ctx.shadowColor = `rgba(${r}, ${g}, ${b}, 0.4)`;
      }
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, TWO_PI);
      ctx.fill();
      ctx.restore();

      // --- Highlight core: brighter center with shadowBlur glow (source-over) ---
      // Avoids 'lighter' composite which causes white blowout through trail accumulation
      if (glowIntensity > 0) {
        const coreRadius = radius * 0.3;
        const coreGrad = ctx.createRadialGradient(x, y, 0, x, y, coreRadius);
        const coreR = Math.min(255, r + 60);
        const coreG = Math.min(255, g + 60);
        const coreB = Math.min(255, b + 60);
        coreGrad.addColorStop(0, `rgba(${coreR}, ${coreG}, ${coreB}, ${alpha * 0.25})`);
        coreGrad.addColorStop(1, `rgba(${coreR}, ${coreG}, ${coreB}, 0)`);

        ctx.save();
        ctx.shadowBlur = glowIntensity * 0.5;
        ctx.shadowColor = `rgba(${coreR}, ${coreG}, ${coreB}, 0.5)`;
        ctx.fillStyle = coreGrad;
        ctx.beginPath();
        ctx.arc(x, y, coreRadius, 0, TWO_PI);
        ctx.fill();
        ctx.restore();
      }
    }
  }

  /**
   * Spawn, update, and draw sparkle particles near blob centers.
   * Uses a circular pool allocation to avoid GC pressure.
   *
   * Sparkles are tiny white points with very low alpha (0.15-0.40),
   * slight upward drift, and quadratic fade. Spawn rate is driven by
   * treble energy * sparkleAmount.
   */
  _drawSparkles(dt, highEnergy, sparkleAmount) {
    const { ctx, canvas } = this;

    if (sparkleAmount <= 0) return;

    // Spawn new sparkles based on treble energy
    const spawnCount = Math.min(
      MAX_SPARKLE_SPAWN_PER_FRAME,
      Math.floor(highEnergy * sparkleAmount * 8)
    );

    const minDim = Math.min(canvas.width, canvas.height);

    for (let s = 0; s < spawnCount; s++) {
      const particle = this.sparklePool[this._nextSparkle];
      this._nextSparkle = (this._nextSparkle + 1) % MAX_SPARKLES;

      // Place near a random blob's cached screen position (computed in _drawBlobs)
      const blobIdx = Math.floor(Math.random() * this.blobs.length);
      const blob = this.blobs[blobIdx];
      const blobRadius = this.config.blobSize * minDim * blob.baseRadius;
      const blobX = blob._screenX;
      const blobY = blob._screenY;

      // Scatter within blob radius
      const angle = Math.random() * TWO_PI;
      const dist = Math.random() * blobRadius * 0.8;
      particle.x = blobX + Math.cos(angle) * dist;
      particle.y = blobY + Math.sin(angle) * dist;
      particle.life = 1;
      particle.maxLife = 0.3 + Math.random() * 0.7; // 0.3-1.0s lifespan
      particle.size = 0.5 + Math.random() * 1.5;    // 0.5-2px
      particle.alpha = 0.15 + Math.random() * 0.25;  // 0.15-0.40
      particle.active = true;
    }

    // Update and draw all active sparkle particles
    // Use source-over (not lighter) to avoid additive blowout through trail accumulation
    ctx.save();
    ctx.fillStyle = '#ffffff';

    for (let i = 0; i < MAX_SPARKLES; i++) {
      const p = this.sparklePool[i];
      if (!p.active) continue;

      p.life -= dt / p.maxLife;
      if (p.life <= 0) {
        p.active = false;
        continue;
      }

      // Slight upward drift
      p.y -= 12 * dt;

      // Quadratic falloff for natural fade
      const lifeAlpha = p.life * p.life;
      ctx.globalAlpha = p.alpha * lifeAlpha;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * lifeAlpha, 0, TWO_PI);
      ctx.fill();
    }

    ctx.restore();
  }

  /**
   * Clear canvas and reset all runtime state.
   */
  clear() {
    super.clear();
    this.time = 0;
    this.breathPhase = 0;
    this.isFirstFrame = true;

    // Reset sparkles
    for (let i = 0; i < MAX_SPARKLES; i++) {
      this.sparklePool[i].active = false;
    }
    this._nextSparkle = 0;

    // Reinitialize blobs
    this._initBlobs();

    // Clear trail buffer so stale frames don't flash on restart
    if (this._trailCtx && this._trailCanvas) {
      this._trailCtx.clearRect(0, 0, this._trailCanvas.width, this._trailCanvas.height);
    }

    // Invalidate vignette cache
    this._vignetteGradient = null;
  }
}

// Register with the visualizer registry
VisualizerRegistry.register(BreathingNebula);

export { BreathingNebula };
export default BreathingNebula;
